import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead,
} from "@amzn/meridian/data-table";
import Input from "@amzn/meridian/input";
import Tag from "@amzn/meridian/tag";
import Divider from "@amzn/meridian/divider";

import { useAppDispatch, useAppSelector } from "../../../store";
import { fetchFlowPathPercentageConfig } from "../../../store/slices/settingsSlice";
import { selectStationCode } from "../../../store/slices/shiftPlanSlice";
import { showError, showNotification } from "../../../store/slices/errorSlice";
import { validateFlowPathPercentage } from "../utils";
import {
  updateWarehouseOperationalConfig,
  getFlowPathPercentageConfig,
  listConfigDrafts,
  promoteConfig,
} from "../api";
import {
  FlowPathPercentageConfig,
  WarehouseOperationalConfigChange,
  DraftEntry,
} from "../types";

const TABLE_COLUMNS = ["Department", "Process Path", "Function", "Flow Path %", "Actions"];

const DRAFT_TABLE_COLUMNS = ["Action", "Department", "Process Path", "Function", "Flow Path %"];

interface NewRow {
  id: string;
  department: string;
  processPath: string;
  function: string;
  percentage: string;
}

const buildKey = (config: FlowPathPercentageConfig): string =>
  `${config.department}|${config.processPath}|${config.function}`;

const getStatusTagType = (action?: string): "success" | "warning" | "error" | undefined => {
  if (action === "CREATE") return "success";
  if (action === "UPDATE") return "warning";
  if (action === "DELETE") return "error";
  return undefined;
};

const getCellBgColor = (action?: string): string | undefined => {
  if (action === "CREATE") return "#e6f9e6";
  if (action === "UPDATE") return "#fff8e1";
  if (action === "DELETE") return "#fde8e8";
  return undefined;
};

// TODO: [Tech Debt] Extract DraftManagementBar (stage version search, View Drafts, Promote)
// into a shared component used by both IndirectHCTab and FlowPathPercentTab.
// See: https://taskei.amazon.dev/rooms/4d9150b8-4d14-4e72-892c-e82220aa16eb/overview
const FlowPathPercentTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const configs = useAppSelector((state) => state.settings.flowPathPercent.configs);
  const loading = useAppSelector((state) => state.settings.flowPathPercent.loading);
  const error = useAppSelector((state) => state.settings.flowPathPercent.error);
  const stationCode = useAppSelector(selectStationCode);

  const [pendingChanges, setPendingChanges] = useState<Map<string, number>>(new Map());
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());
  const [infoBanner, setInfoBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // New rows (CREATE)
  const [newRows, setNewRows] = useState<NewRow[]>([]);

  // Deleted keys (DELETE)
  const [deletedKeys, setDeletedKeys] = useState<Set<string>>(new Set());

  // Search / draft preview state
  const [searchVersion, setSearchVersion] = useState("");
  const [draftPreview, setDraftPreview] = useState<FlowPathPercentageConfig[] | null>(null);
  const [draftVersionName, setDraftVersionName] = useState<string | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);

  // Drafts list state
  const [drafts, setDrafts] = useState<DraftEntry[]>([]);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsLoading, setDraftsLoading] = useState(false);

  // Promote state
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    if (stationCode) {
      dispatch(fetchFlowPathPercentageConfig(stationCode));
      setPendingChanges(new Map());
      setValidationErrors(new Map());
      setInfoBanner(null);
      setNewRows([]);
      setDeletedKeys(new Set());
    }
  }, [dispatch, stationCode]);

  const hasPendingChanges = pendingChanges.size > 0 || newRows.length > 0 || deletedKeys.size > 0;
  const hasValidationErrors = validationErrors.size > 0;

  const handlePercentageChange = (key: string, value: string) => {
    const validation = validateFlowPathPercentage(value);

    if (!validation.isValid) {
      setValidationErrors((prev) => new Map(prev).set(key, validation.error!));
      if (value.trim().length > 0 && /^-?\d+$/.test(value.trim())) {
        setPendingChanges((prev) => new Map(prev).set(key, Number(value.trim())));
      }
      return;
    }

    setValidationErrors((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setPendingChanges((prev) => new Map(prev).set(key, Number(value.trim())));
  };

  // --- Add row ---
  const handleAddRow = () => {
    const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setNewRows((prev) => [{ id, department: "", processPath: "", function: "", percentage: "" }, ...prev]);
  };

  const handleNewRowChange = (id: string, field: keyof Omit<NewRow, "id">, value: string) => {
    setNewRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleRemoveNewRow = (id: string) => {
    setNewRows((prev) => prev.filter((row) => row.id !== id));
  };

  // --- Delete row ---
  const handleDeleteRow = (key: string) => {
    setDeletedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const handleUndoDelete = (key: string) => {
    setDeletedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // --- Save ---
  const handleSave = async () => {
    if (!stationCode || !hasPendingChanges || hasValidationErrors) return;

    setSaving(true);
    try {
      const changes: WarehouseOperationalConfigChange[] = [];

      // UPDATE changes: existing rows with modified percentage
      for (const [key, percentage] of Array.from(pendingChanges.entries())) {
        if (deletedKeys.has(key)) continue; // skip if also deleted
        const [department, processPath, fn] = key.split("|");
        changes.push({
          action: "UPDATE",
          department,
          processPath,
          function: fn,
          configKey: "directFlowPathPercent",
          configValue: String(percentage),
        });
      }

      // CREATE changes: new rows
      for (const row of newRows) {
        if (!row.department || !row.processPath || !row.function || !row.percentage) continue;
        changes.push({
          action: "CREATE",
          department: row.department,
          processPath: row.processPath,
          function: row.function,
          configKey: "directFlowPathPercent",
          configValue: row.percentage,
        });
      }

      // DELETE changes: deleted existing rows
      for (const key of Array.from(deletedKeys)) {
        const [department, processPath, fn] = key.split("|");
        const config = configs.find(
          (c) => c.department === department && c.processPath === processPath && c.function === fn
        );
        if (config) {
          changes.push({
            action: "DELETE",
            department,
            processPath,
            function: fn,
            configKey: "directFlowPathPercent",
            configValue: String(config.flowPathPercentage),
          });
        }
      }

      if (changes.length === 0) {
        setSaving(false);
        return;
      }

      const result = await updateWarehouseOperationalConfig({
        warehouseId: stationCode,
        updatedBy: "",
        changes,
      });

      if (result.status === "PROMOTED") {
        dispatch(showNotification({ message: result.message || "Changes promoted successfully", type: "success" }));
        setPendingChanges(new Map());
        setValidationErrors(new Map());
        setInfoBanner(null);
        setNewRows([]);
        setDeletedKeys(new Set());
        dispatch(fetchFlowPathPercentageConfig(stationCode));
      } else if (result.status === "PENDING_APPROVAL") {
        setInfoBanner(
          `Draft saved (${result.stageVersionName}). Requires approval from an authorized user.`
        );
        setPendingChanges(new Map());
        setValidationErrors(new Map());
        setNewRows([]);
        setDeletedKeys(new Set());
      } else if (result.status === "FAILED") {
        dispatch(showError({ message: result.message || "Failed to save changes" }));
      }
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to save changes" }));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPendingChanges(new Map());
    setValidationErrors(new Map());
    setInfoBanner(null);
    setNewRows([]);
    setDeletedKeys(new Set());
  };

  // --- Search / Draft Preview ---
  const handleSearch = async (versionOverride?: string) => {
    if (!stationCode) return;
    const version = versionOverride ?? searchVersion;
    setDraftLoading(true);
    try {
      const resp = await getFlowPathPercentageConfig(stationCode, version || undefined);
      if (version) {
        setDraftPreview(resp.flowPathEntries || []);
        setDraftVersionName(version);
      } else {
        setDraftPreview(null);
        setDraftVersionName(null);
        dispatch(fetchFlowPathPercentageConfig(stationCode));
      }
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to fetch config" }));
      setDraftPreview(null);
      setDraftVersionName(null);
    } finally {
      setDraftLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchVersion("");
    setDraftPreview(null);
    setDraftVersionName(null);
  };

  // --- Drafts List ---
  const handleViewDrafts = async () => {
    if (!stationCode) return;
    setDraftsLoading(true);
    try {
      const resp = await listConfigDrafts(stationCode, "FLOW_PATH_PERCENT");
      setDrafts(resp.drafts || []);
      setDraftsOpen(true);
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to list drafts" }));
    } finally {
      setDraftsLoading(false);
    }
  };

  const handleSelectDraft = (versionName: string) => {
    setSearchVersion(versionName);
    setDraftsOpen(false);
    handleSearch(versionName);
  };

  // --- Promote ---
  const handlePromote = async () => {
    if (!stationCode || !draftVersionName) return;
    setPromoting(true);
    try {
      const resp = await promoteConfig({
        warehouseId: stationCode,
        configType: "FLOW_PATH_PERCENT",
        stageVersionName: draftVersionName,
        promotedBy: "",
      });
      dispatch(showNotification({ message: resp.message, type: "success" }));
      handleClearSearch();
      dispatch(fetchFlowPathPercentageConfig(stationCode));
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to promote" }));
    } finally {
      setPromoting(false);
    }
  };

  if (!stationCode) {
    return (
      <Box spacingInset="400">
        <Alert type="neutral">
          Please select a warehouse to view flow path percentage configuration.
        </Alert>
      </Box>
    );
  }

  // --- Draft preview mode ---
  if (draftPreview !== null) {
    return (
      <Box spacingInset="400">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Heading level={3} type="h300">Draft Preview: {draftVersionName}</Heading>
          <Row spacing="300">
            <Button type="tertiary" onClick={handleClearSearch}>Back to Live</Button>
            <Button type="primary" onClick={handlePromote} disabled={promoting}>
              {promoting ? "Promoting..." : "Promote"}
            </Button>
          </Row>
        </Row>
        <Box spacingInset="300 none">
          <DataTable showRowDividers>
            <DataTableHead>
              <DataTableRow>
                {DRAFT_TABLE_COLUMNS.map((col) => (
                  <DataTableHeader key={col}>{col}</DataTableHeader>
                ))}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {draftPreview.length === 0 ? (
                <DataTableRow>
                  <DataTableCell colSpan={DRAFT_TABLE_COLUMNS.length}>
                    <Text color="secondary">No configurations in this draft.</Text>
                  </DataTableCell>
                </DataTableRow>
              ) : (
                draftPreview.map((config, idx) => (
                  <DataTableRow key={`draft-${idx}`}>
                    <DataTableCell>
                      <Tag type="warning">DRAFT</Tag>
                    </DataTableCell>
                    <DataTableCell>{config.department}</DataTableCell>
                    <DataTableCell>{config.processPath}</DataTableCell>
                    <DataTableCell>{config.function}</DataTableCell>
                    <DataTableCell>{config.flowPathPercentage}%</DataTableCell>
                  </DataTableRow>
                ))
              )}
            </DataTableBody>
          </DataTable>
        </Box>
      </Box>
    );
  }

  // --- Main live view ---
  return (
    <Box spacingInset="400">
      {/* Search bar */}
      <Row spacing="300" alignmentVertical="bottom">
        <Input
          value={searchVersion}
          onChange={(v: string) => setSearchVersion(v)}
          label="Stage Version (optional)"
          placeholder="e.g. Stage Version Name"
          size="medium"
        />
        <Button type="secondary" onClick={() => handleSearch()} disabled={draftLoading}>
          {draftLoading ? "Searching..." : "Search"}
        </Button>
        <Button type="tertiary" onClick={draftsOpen ? () => setDraftsOpen(false) : handleViewDrafts} disabled={draftsLoading}>
          {draftsLoading ? "Loading..." : draftsOpen ? "Close Drafts" : "View Drafts"}
        </Button>
      </Row>

      {/* Drafts dropdown */}
      {draftsOpen && (
        <Box spacingInset="300 none">
          <Alert type="neutral">
            <Text type="b200">Pending Drafts:</Text>
            {drafts.length === 0 ? (
              <Text>No pending drafts.</Text>
            ) : (
              drafts.map((d) => (
                <Row key={d.stageVersionName} spacing="300" alignmentVertical="center">
                  <Text>{d.stageVersionName} — by {d.createdBy} ({d.changeCount} changes)</Text>
                  <Button type="tertiary" size="small" onClick={() => handleSelectDraft(d.stageVersionName)}>
                    Preview
                  </Button>
                </Row>
              ))
            )}
          </Alert>
        </Box>
      )}

      {/* Info banner for pending approval */}
      {infoBanner && (
        <Box spacingInset="300 none">
          <Alert type="neutral">
            <Row alignmentHorizontal="justify" alignmentVertical="center">
              <Text>{infoBanner}</Text>
              <Button type="tertiary" size="small" onClick={() => setInfoBanner(null)}>Dismiss</Button>
            </Row>
          </Alert>
        </Box>
      )}

      {/* Header + action buttons */}
      <Box spacingInset="400 none">
        <Divider />
      </Box>
      <Box spacingInset="300 none">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Heading level={3} type="h300">
            Flow Path Percentage Configuration
            {hasPendingChanges && (
              <Text color="secondary">
                {" "}({pendingChanges.size + newRows.length + deletedKeys.size} pending)
              </Text>
            )}
          </Heading>
          <Row spacing="300">
            <Button type="primary" onClick={handleAddRow} disabled={loading}>Add</Button>
            {hasPendingChanges && (
              <>
                <Button type="primary" onClick={handleSave} disabled={saving || hasValidationErrors}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button type="tertiary" onClick={handleDiscard}>Discard Changes</Button>
              </>
            )}
          </Row>
        </Row>
      </Box>

      {error && (
        <Box spacingInset="300 none">
          <Alert type="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box spacingInset="500">
          <Text color="secondary">Loading flow path percentage configuration...</Text>
        </Box>
      ) : (
        <Box spacingInset="300 none">
          <DataTable showRowDividers>
            <DataTableHead>
              <DataTableRow>
                {hasPendingChanges && <DataTableHeader>Status</DataTableHeader>}
                {TABLE_COLUMNS.map((col) => (
                  <DataTableHeader key={col}>{col}</DataTableHeader>
                ))}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {/* New rows (CREATE) at the top */}
              {newRows.map((row) => {
                const bgColor = getCellBgColor("CREATE");
                return (
                  <DataTableRow key={row.id}>
                    {hasPendingChanges && (
                      <DataTableCell backgroundColor={bgColor}>
                        <Tag type="success">CREATE</Tag>
                      </DataTableCell>
                    )}
                    <DataTableCell backgroundColor={bgColor}>
                      <Input
                        value={row.department}
                        onChange={(v: string) => handleNewRowChange(row.id, "department", v)}
                        placeholder="Department"
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell backgroundColor={bgColor}>
                      <Input
                        value={row.processPath}
                        onChange={(v: string) => handleNewRowChange(row.id, "processPath", v)}
                        placeholder="Process Path"
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell backgroundColor={bgColor}>
                      <Input
                        value={row.function}
                        onChange={(v: string) => handleNewRowChange(row.id, "function", v)}
                        placeholder="Function"
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell backgroundColor={bgColor}>
                      <Input
                        type="number"
                        value={row.percentage}
                        onChange={(v: string) => handleNewRowChange(row.id, "percentage", v)}
                        placeholder="0-100"
                        size="small"
                        suffix="%"
                      />
                    </DataTableCell>
                    <DataTableCell backgroundColor={bgColor}>
                      <Button type="tertiary" size="small" onClick={() => handleRemoveNewRow(row.id)}>
                        Remove
                      </Button>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}

              {/* Existing rows */}
              {configs.length === 0 && newRows.length === 0 ? (
                <DataTableRow>
                  <DataTableCell colSpan={TABLE_COLUMNS.length + (hasPendingChanges ? 1 : 0)}>
                    <Text color="secondary">No flow path percentage configuration found.</Text>
                  </DataTableCell>
                </DataTableRow>
              ) : (
                configs.map((config) => {
                  const key = buildKey(config);
                  const isDeleted = deletedKeys.has(key);
                  const isModified = !isDeleted && pendingChanges.has(key);
                  const changeAction = isDeleted ? "DELETE" : isModified ? "UPDATE" : undefined;
                  const bgColor = getCellBgColor(changeAction);
                  const tagType = getStatusTagType(changeAction);
                  const currentValue = isModified
                    ? String(pendingChanges.get(key))
                    : String(config.flowPathPercentage);
                  const validationError = validationErrors.get(key);

                  return (
                    <DataTableRow key={key}>
                      {hasPendingChanges && (
                        <DataTableCell backgroundColor={bgColor}>
                          {changeAction
                            ? <Tag type={tagType!}>{changeAction}</Tag>
                            : "—"}
                        </DataTableCell>
                      )}
                      <DataTableCell backgroundColor={bgColor}>{config.department}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{config.processPath}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{config.function}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>
                        {isDeleted ? (
                          <Text>{config.flowPathPercentage}%</Text>
                        ) : (
                          <Input
                            type="number"
                            value={currentValue}
                            onChange={(value: string) => handlePercentageChange(key, value)}
                            size="small"
                            suffix="%"
                            error={!!validationError}
                            errorMessage={validationError}
                          />
                        )}
                      </DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>
                        {isDeleted ? (
                          <Button type="tertiary" size="small" onClick={() => handleUndoDelete(key)}>
                            Undo
                          </Button>
                        ) : (
                          <Row spacing="200">
                            <Button type="tertiary" size="small" onClick={() => handleDeleteRow(key)}>
                              Delete
                            </Button>
                          </Row>
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })
              )}
            </DataTableBody>
          </DataTable>
        </Box>
      )}
    </Box>
  );
};

export default FlowPathPercentTab;
